import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  TagsInput,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  ApiError,
  api,
  type CampaignResponse,
  type CascadeUsageResponse,
  type ClientResponse,
  type InviteCreatedResponse,
  type RunResponse,
} from "../lib/api";
import { isUnlocked } from "../lib/session-key";

type RunStatus = "active" | "overdue" | "closed";

type UnifiedRow =
  | { type: "run"; campaign: CampaignResponse; run: RunResponse; runStatus: RunStatus }
  | { type: "not_launched"; campaign: CampaignResponse };

const STATUS_ORDER: Record<RunStatus | "not_launched", number> = {
  overdue: 0,
  active: 1,
  not_launched: 2,
  closed: 3,
};

function buildRows(campaigns: CampaignResponse[], runs: RunResponse[]): UnifiedRow[] {
  const now = Date.now();
  const rows: UnifiedRow[] = [];

  for (const campaign of campaigns) {
    const campaignRuns = runs.filter((r) => r.campaign_id === campaign.campaign_id);
    if (campaignRuns.length === 0) {
      rows.push({ type: "not_launched", campaign });
    } else {
      for (const run of campaignRuns) {
        const runStatus: RunStatus = run.closed_at
          ? "closed"
          : new Date(run.deadline_at).getTime() < now
            ? "overdue"
            : "active";
        rows.push({ type: "run", campaign, run, runStatus });
      }
    }
  }

  rows.sort((a, b) => {
    const aKey = a.type === "not_launched" ? "not_launched" : a.runStatus;
    const bKey = b.type === "not_launched" ? "not_launched" : b.runStatus;
    if (STATUS_ORDER[aKey] !== STATUS_ORDER[bKey]) {
      return STATUS_ORDER[aKey] - STATUS_ORDER[bKey];
    }
    if (a.type === "run" && b.type === "run") {
      return new Date(a.run.deadline_at).getTime() - new Date(b.run.deadline_at).getTime();
    }
    if (a.type === "not_launched" && b.type === "not_launched") {
      return a.campaign.name.localeCompare(b.campaign.name, "he");
    }
    return 0;
  });

  return rows;
}

export function HomeScreen() {
  const [campaigns, setCampaigns] = useState<CampaignResponse[] | null>(null);
  const [runs, setRuns] = useState<RunResponse[] | null>(null);
  const [securityFlags, setSecurityFlags] = useState<{
    has2FA: boolean | null;
    keypair_ready: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignResponse | null>(null);
  const [deletingCampaign, setDeletingCampaign] = useState<CampaignResponse | null>(null);
  const [kickingOffCampaign, setKickingOffCampaign] = useState<CampaignResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [campaignsList, runsList] = await Promise.all([
        api.campaignsList(),
        api.runsList(true),
      ]);
      setCampaigns(campaignsList.campaigns);
      setRuns(runsList.runs);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה בטעינה");
    }
  }, []);

  useEffect(() => {
    refresh();
    api.twoFAStatus()
      .then((r) => setSecurityFlags((prev) => ({ ...prev, has2FA: r.enabled, keypair_ready: prev?.keypair_ready ?? false })))
      .catch(() => {});
    api.keypairStatus()
      .then((r) => setSecurityFlags((prev) => ({ ...prev, keypair_ready: r.ready ?? false, has2FA: prev?.has2FA ?? null })))
      .catch(() => {});
  }, [refresh]);

  const loading = campaigns === null || runs === null;
  const rows = loading ? [] : buildRows(campaigns, runs);

  return (
    <Stack gap="xl">
      {/* ── Security alerts ── */}
      {securityFlags && !securityFlags.keypair_ready && (
        <Alert color="red" title="חסר מפתח הצפנה">
          <Group justify="space-between">
            <Text size="sm">ללא מפתח, הדשבורד לא יכול לפענח קבצים שהלקוחות שולחים.</Text>
            <Anchor component={Link} to="/setup">הגדר עכשיו ←</Anchor>
          </Group>
        </Alert>
      )}
      {securityFlags?.keypair_ready && !isUnlocked() && (
        <Alert color="yellow" title="מפתח נעול">
          <Group justify="space-between">
            <Text size="sm">הזן את סיסמת ההצפנה כדי שהדשבורד יוכל לפענח קבצים חדשים.</Text>
            <Anchor component={Link} to="/setup">פתח ←</Anchor>
          </Group>
        </Alert>
      )}
      {securityFlags?.has2FA === false && (
        <Alert color="yellow">
          <Group justify="space-between">
            <Text size="sm">לחשבון זה אין 2FA פעיל. מומלץ להפעיל.</Text>
            <Anchor component={Link} to="/2fa/setup">הגדר עכשיו ←</Anchor>
          </Group>
        </Alert>
      )}

      {/* ── Unified campaigns table ── */}
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>קמפיינים</Title>
          <Button onClick={() => setOpenCreate(true)}>קמפיין חדש</Button>
        </Group>

        {error && <Alert color="red">{error}</Alert>}

        {loading ? (
          <Loader size="sm" />
        ) : rows.length === 0 ? (
          <Paper p="xl" withBorder>
            <Stack align="center">
              <Text c="dimmed">עדיין לא נוצר קמפיין.</Text>
              <Button onClick={() => setOpenCreate(true)}>צור קמפיין ראשון</Button>
            </Stack>
          </Paper>
        ) : (
          <Paper withBorder>
            <Table dir="rtl" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>שם קמפיין</Table.Th>
                  <Table.Th>סטטוס</Table.Th>
                  <Table.Th>דדליין</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.type === "run" ? row.run.run_id : `nolaunched-${row.campaign.campaign_id}`}>
                    <Table.Td>
                      <Text fw={500}>{row.campaign.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <RunStatusBadge row={row} />
                    </Table.Td>
                    <Table.Td>
                      {row.type === "run" ? (
                        <Text size="sm">{new Date(row.run.deadline_at).toLocaleDateString("he-IL")}</Text>
                      ) : (
                        <Text size="sm" c="dimmed">—</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {row.type === "run" ? (
                          <Anchor component={Link} to={`/runs/${row.run.run_id}`} size="sm">
                            סטטוס הגשות ←
                          </Anchor>
                        ) : (
                          <Button size="xs" onClick={() => setKickingOffCampaign(row.campaign)}>
                            הפעל קמפיין
                          </Button>
                        )}
                        <Button size="xs" variant="subtle" onClick={() => setEditingCampaign(row.campaign)}>
                          ערוך
                        </Button>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="מחק קמפיין"
                          onClick={() => setDeletingCampaign(row.campaign)}
                        >
                          🗑
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      {/* ── Modals ── */}
      {kickingOffCampaign && (
        <RunKickoffModal
          campaign={kickingOffCampaign}
          onClose={() => setKickingOffCampaign(null)}
          onCreated={async () => { setKickingOffCampaign(null); await refresh(); }}
        />
      )}
      <CampaignFormModal
        opened={openCreate}
        onClose={() => setOpenCreate(false)}
        onSaved={async () => { setOpenCreate(false); await refresh(); }}
      />
      {editingCampaign && (
        <CampaignEditModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSaved={async () => { setEditingCampaign(null); await refresh(); }}
        />
      )}
      {deletingCampaign && (
        <CampaignDeleteModal
          campaign={deletingCampaign}
          onClose={() => setDeletingCampaign(null)}
          onDeleted={async () => { setDeletingCampaign(null); await refresh(); }}
        />
      )}
    </Stack>
  );
}

function RunStatusBadge({ row }: { row: UnifiedRow }) {
  if (row.type === "not_launched") return <Badge color="blue" variant="light">לא הופעל</Badge>;
  const map: Record<RunStatus, { label: string; color: string }> = {
    active: { label: "פעיל", color: "green" },
    overdue: { label: "באיחור", color: "red" },
    closed: { label: "הסתיים", color: "gray" },
  };
  const { label, color } = map[row.runStatus];
  return <Badge color={color} variant="light">{label}</Badge>;
}

function RunKickoffModal({
  campaign,
  onClose,
  onCreated,
}: {
  campaign: CampaignResponse;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const [clients, setClients] = useState<ClientResponse[] | null>(null);
  const [deadlineDays, setDeadlineDays] = useState<string | number>(
    campaign.default_deadline_days ?? 14,
  );
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    invites: InviteCreatedResponse[];
    failures: { client_id: string; error: string }[];
  } | null>(null);

  useEffect(() => {
    api.clientsList()
      .then((r) => setClients(r.clients))
      .catch((err) => setError(err instanceof ApiError ? err.detail : "שגיאה בטעינה"));
  }, []);

  const toggleClient = (id: string) =>
    setSelectedClientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleKickoff = async () => {
    if (selectedClientIds.length === 0) return;
    setBusy(true); setError(null); setCreated(null);
    try {
      const days = typeof deadlineDays === "string" ? parseInt(deadlineDays || "14", 10) : deadlineDays;
      const run = await api.runCreate(campaign.campaign_id, "", days || 14);
      const bulk = await api.inviteBulk(run.run_id, selectedClientIds);
      setCreated({ invites: bulk.invites, failures: bulk.failures });
      setSelectedClientIds([]);
      await onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה לא צפויה");
    } finally {
      setBusy(false);
    }
  };

  const delivered = created?.invites.filter((i) => i.email_delivered) ?? [];
  const noEmail = created?.invites.filter((i) => i.delivery_skipped_reason === "no_email") ?? [];
  const sendFailed = created?.invites.filter((i) => i.delivery_skipped_reason === "send_failed") ?? [];

  return (
    <Modal opened onClose={onClose} title={`הפעל קמפיין — ${campaign.name}`} size="lg">
      <Stack>
        <Text c="dimmed" size="sm">בחר דדליין ולקוחות. כל אחד יקבל קישור הזמנה משלו.</Text>

        <NumberInput
          label="תוך כמה ימים?"
          value={deadlineDays}
          onChange={setDeadlineDays}
          min={1}
          max={180}
          style={{ maxWidth: 200 }}
        />

        {clients === null ? (
          <Loader size="sm" />
        ) : clients.length === 0 ? (
          <Alert color="yellow">הוסף קודם לקוחות בכרטיסיית "לקוחות".</Alert>
        ) : (
          <div>
            <Group justify="space-between" mb={4}>
              <Text fw={500} size="sm">לקוחות ({selectedClientIds.length} נבחרו)</Text>
              <Group gap={6}>
                <Button size="xs" variant="subtle" onClick={() => setSelectedClientIds(clients.map((c) => c.client_id))}>בחר הכל</Button>
                <Button size="xs" variant="subtle" onClick={() => setSelectedClientIds([])}>נקה</Button>
              </Group>
            </Group>
            <Paper withBorder p="sm" mah={240} style={{ overflowY: "auto" }}>
              <Stack gap={4}>
                {clients.map((c) => (
                  <Checkbox
                    key={c.client_id}
                    label={
                      <Group gap={6}>
                        <Text size="sm">{c.name}</Text>
                        {c.phone_e164 && <Text c="dimmed" size="xs" dir="ltr">{c.phone_e164}</Text>}
                      </Group>
                    }
                    checked={selectedClientIds.includes(c.client_id)}
                    onChange={() => toggleClient(c.client_id)}
                  />
                ))}
              </Stack>
            </Paper>
          </div>
        )}

        {error && <Alert color="red">{error}</Alert>}

        {created && (
          <Stack gap={6}>
            <Alert color="green">✉️ {delivered.length} הזמנות נשלחו. מערכת התזכורות תפעל ברקע.</Alert>
            {noEmail.length > 0 && (
              <Alert color="yellow">⚠️ {noEmail.length} לקוחות ללא אימייל — העתק קישורים ידנית:</Alert>
            )}
            {sendFailed.length > 0 && (
              <Alert color="orange">⚠️ {sendFailed.length} שליחות נכשלו — העתק קישורים ידנית:</Alert>
            )}
            {[...noEmail, ...sendFailed].map((inv) => (
              <Code block dir="ltr" key={inv.invite_id}>{inv.invite_url}</Code>
            ))}
            {created.failures.map((f) => (
              <Badge key={f.client_id} color="red" variant="light">{f.client_id}: {f.error}</Badge>
            ))}
          </Stack>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>סגור</Button>
          {!created && (
            <Button
              onClick={handleKickoff}
              loading={busy}
              disabled={selectedClientIds.length === 0}
            >
              הפעל קמפיין ושלח הזמנות
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

function CampaignEditModal({
  campaign, onClose, onSaved,
}: { campaign: CampaignResponse; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [name, setName] = useState(campaign.name);
  const [docs, setDocs] = useState<string[]>(campaign.document_list);
  const [deadline, setDeadline] = useState<number | string>(campaign.default_deadline_days ?? "");
  const [selectedDays, setSelectedDays] = useState<number[]>(
    campaign.default_reminder_schedule_days ?? [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deadlineNum = typeof deadline === "number" && deadline > 0
    ? deadline
    : typeof deadline === "string" && deadline.trim() !== ""
      ? parseInt(deadline, 10)
      : null;

  const suggested = deadlineNum ? suggestedDaysForDeadline(deadlineNum) : null;

  const toggleDay = (day: number) =>
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => b - a),
    );

  const save = async () => {
    if (!name.trim() || docs.length === 0) { setError("שם ורשימת מסמכים נדרשים"); return; }
    setBusy(true); setError(null);
    try {
      await Promise.all([
        api.campaignUpdate(campaign.campaign_id, { name: name.trim(), document_list: docs }),
        api.campaignSetDefaults(campaign.campaign_id, {
          default_reminder_schedule_days: selectedDays.length > 0 ? selectedDays : null,
          default_deadline_days: deadlineNum && Number.isFinite(deadlineNum) ? deadlineNum : null,
        }),
      ]);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שמירה נכשלה");
    } finally { setBusy(false); }
  };

  return (
    <Modal opened onClose={onClose} title={`עריכת קמפיין — ${campaign.name}`} size="md">
      <Stack gap="lg">
        <Stack gap="xs">
          <TextInput label="שם הקמפיין" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
          <TagsInput label="מסמכים מבוקשים" value={docs} onChange={setDocs} placeholder="הוסף מסמך… (Enter לאישור)" required />
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={500}>כמה ימים יש ללקוחות להגיש מסמכים?</Text>
          <NumberInput
            value={deadline}
            onChange={setDeadline}
            min={1}
            max={180}
            placeholder="14"
            suffix=" ימים"
            style={{ maxWidth: 160 }}
          />
        </Stack>

        <Stack gap={6}>
          <Group justify="space-between" align="flex-end">
            <div>
              <Text size="sm" fw={500}>מתי לשלוח תזכורות?</Text>
              <Text size="xs" c="dimmed">ימים לפני הדדליין</Text>
            </div>
            {suggested && (
              <Button size="xs" variant="subtle" onClick={() => setSelectedDays(suggested)}>
                בחר לפי דדליין
              </Button>
            )}
          </Group>
          <Group gap={8}>
            {REMINDER_DAY_OPTIONS.map((day) => (
              <Button
                key={day}
                size="xs"
                variant={selectedDays.includes(day) ? "filled" : "light"}
                onClick={() => toggleDay(day)}
                style={{ minWidth: 60 }}
              >
                {day} {day === 1 ? "יום" : "ימים"}
              </Button>
            ))}
          </Group>
          {selectedDays.length === 0 ? (
            <Text size="xs" c="dimmed">ברירת מחדל: 7, 3 ו-1 ימים לפני הדדליין</Text>
          ) : (
            <Text size="xs" c="blue">
              תזכורות יישלחו {selectedDays.slice().sort((a, b) => b - a).join(", ")} ימים לפני הדדליין
            </Text>
          )}
        </Stack>

        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>ביטול</Button>
          <Button onClick={save} loading={busy}>שמור</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CampaignDeleteModal({
  campaign, onClose, onDeleted,
}: { campaign: CampaignResponse; onClose: () => void; onDeleted: () => void | Promise<void> }) {
  const [usage, setUsage] = useState<CascadeUsageResponse | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.campaignUsage(campaign.campaign_id).then(setUsage).catch((err) =>
      setError(err instanceof ApiError ? err.detail : "טעינת נתונים נכשלה"),
    );
  }, [campaign.campaign_id]);

  const expected = `מחק ${campaign.name}`;
  const ready = confirmText.trim() === expected.trim();
  const hasData = usage !== null && (usage.runs > 0 || usage.invites > 0 || usage.files > 0);

  const doDelete = async () => {
    setBusy(true); setError(null);
    try {
      await api.campaignDelete(campaign.campaign_id);
      await onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "מחיקה נכשלה");
    } finally { setBusy(false); }
  };

  return (
    <Modal opened onClose={onClose} title={`מחיקת קמפיין — ${campaign.name}`} size="md">
      <Stack>
        <Alert color="red" title="פעולה בלתי-הפיכה">
          מחיקה תסיר לצמיתות את הקמפיין, כל הקמפיינים הפעילים שלו, ההזמנות, וכל הקבצים שהועלו.
        </Alert>
        {usage === null && !error ? <Loader size="sm" /> : usage !== null ? (
          <Paper p="sm" withBorder>
            <Stack gap={4}>
              <Text size="sm" fw={500}>השפעה צפויה:</Text>
              <Text size="sm">• {usage.runs} קמפיינים פעילים</Text>
              <Text size="sm">• {usage.invites} הזמנות</Text>
              <Text size="sm">• {usage.files} קבצים מוצפנים ({(usage.bytes_total / 1024 / 1024).toFixed(1)} MB)</Text>
            </Stack>
          </Paper>
        ) : null}
        {hasData && (
          <TextInput label={`הקלד "${expected}" כדי לאשר`} value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)} placeholder={expected} dir="rtl" autoFocus />
        )}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>ביטול</Button>
          <Button color="red" onClick={doDelete} loading={busy} disabled={hasData && !ready}>מחק לצמיתות</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const REMINDER_DAY_OPTIONS = [1, 2, 3, 5, 7, 10, 14, 21, 30];

function suggestedDaysForDeadline(deadlineDays: number): number[] {
  if (deadlineDays <= 3) return [1];
  if (deadlineDays <= 7) return [5, 2, 1];
  if (deadlineDays <= 14) return [7, 3, 1];
  if (deadlineDays <= 30) return [14, 7, 3, 1];
  return [30, 14, 7, 3, 1];
}

function CampaignFormModal({
  opened, onClose, onSaved,
}: { opened: boolean; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [name, setName] = useState("");
  const [docs, setDocs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<{ template_id: string; name: string; documents: string[] }[]>([]);
  const [templatePick, setTemplatePick] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setName(""); setDocs([]); setError(null); setTemplatePick(null);
      api.templatesList().then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
    }
  }, [opened]);

  useEffect(() => {
    if (!templatePick) return;
    const t = templates.find((x) => x.template_id === templatePick);
    if (!t) return;
    if (!name.trim()) setName(t.name);
    setDocs(t.documents);
  }, [templatePick, templates]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (docs.length === 0) { setError("הוסף לפחות מסמך אחד"); return; }
    setBusy(true); setError(null);
    try {
      await api.campaignCreate(name.trim(), docs);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "שגיאה לא צפויה");
    } finally { setBusy(false); }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="קמפיין חדש" size="lg" withinPortal={false}>
      <form onSubmit={handleSubmit}>
        <Stack>
          {templates.length > 0 && (
            <Select
              label="התחל מתבנית קיימת (אופציונלי)"
              description="בוחר תבנית ימלא מראש את שם הקמפיין ורשימת המסמכים — תוכל לערוך אחר כך."
              data={templates.map((t) => ({ value: t.template_id, label: `${t.name} (${t.documents.length} מסמכים)` }))}
              value={templatePick} onChange={setTemplatePick} placeholder="ללא תבנית" clearable
            />
          )}
          <TextInput
            label="שם הקמפיין" value={name} onChange={(e) => setName(e.target.value)}
            placeholder='למשל "דוחות חודשיים"' required autoFocus
          />
          <TagsInput
            label="מסמכים מבוקשים" value={docs} onChange={setDocs}
            placeholder="הוסף מסמך… (Enter לאישור)" required
          />
          {error && <Alert color="red">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>ביטול</Button>
            <Button type="submit" loading={busy} disabled={!name.trim()}>שמור</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
